import { z } from 'zod';
import { modLoaderOf } from './addons.js';
import { SoftwareUnavailableError, type FetchJson, type SoftwareProvider } from './providers.js';
import type { SoftwareSpec } from './domain.js';
const versionsSchema = z.array(z.object({ name: z.string().min(1) }));
const buildsSchema = z.array(
  z.object({
    id: z.number().int().positive(),
    file_sha256: z.string().regex(/^[a-fA-F0-9]{64}$/),
    loader: z.object({ neoforge_version: z.string().optional(), forge_version: z.string().optional() }),
  }),
);
export function mohistProvider(family: 'youer' | 'asyncyouer' | 'mohist', fetchJson: FetchJson): SoftwareProvider {
  const base = 'https://api.mohistmc.com/project/' + family;
  const builds = async (mc: string) =>
    buildsSchema.parse(await fetchJson(base + '/' + encodeURIComponent(mc) + '/builds'));
  return {
    family,
    layoutFamily: 'vanilla',
    listVersions: async () => versionsSchema.parse(await fetchJson(base + '/versions')).map((v) => v.name),
    listBuilds: async (mc) => (await builds(mc)).map((b) => String(b.id)),
    resolve: async (spec: SoftwareSpec) => {
      if (spec.family !== family) throw new Error('Provider family mismatch');
      const versions = versionsSchema.parse(await fetchJson(base + '/versions'));
      if (!versions.some((v) => v.name === spec.mcVersion))
        throw new SoftwareUnavailableError(
          family === 'asyncyouer' && versions.length === 0
            ? 'AsyncYouer は公開されている版がありません（MohistMC の有料版のみ）'
            : family + ' ' + spec.mcVersion + ' の公開されている版がありません',
        );
      const all = await builds(spec.mcVersion);
      const loaderOfBuild = (b: (typeof all)[number]) =>
        modLoaderOf(family) === 'forge' ? b.loader.forge_version : b.loader.neoforge_version;
      const build = spec.build
        ? all.find((b) => String(b.id) === spec.build)
        : spec.loaderVersion
          ? all.find((b) => loaderOfBuild(b) === spec.loaderVersion)
          : all[0];
      const loaderVersion =
        modLoaderOf(family) === 'forge' ? build?.loader.forge_version : build?.loader.neoforge_version;
      if (!build || !loaderVersion)
        throw new SoftwareUnavailableError(
          family + ' ' + spec.mcVersion + ' の対応するビルドまたはローダーがありません',
        );
      if (spec.loaderVersion && spec.loaderVersion !== loaderVersion)
        throw new SoftwareUnavailableError('指定したローダーの版とハイブリッドのビルドが一致しません');
      return {
        url: base + '/' + encodeURIComponent(spec.mcVersion) + '/builds/' + build.id + '/download',
        fileName: family + '-' + spec.mcVersion + '-' + build.id + '-server.jar',
        hash: { algo: 'sha256', value: build.file_sha256 },
        installMode: 'jar',
        loaderVersion,
        build: String(build.id),
      };
    },
  };
}
